<template>
  <div class="comment-item flex-1 flex flex-col overflow-hidden">
    <!-- 评论顶部信息 -->
    <div class="flex justify-between mb-3">
      <span class="teld-color-gray font-semibold">{{ userNameDisplay }}</span>
      <span :title="comment.CommentTime" class="teld-color-gray">
        {{ comment.CommentTimeRelative }}
      </span>
    </div>

    <!-- 评论内容行 -->
    <div class="comment-content mb-2">
      <div
        v-if="showCommentReference && comment.ReferenceTitle"
        :title="comment.ReferenceTitle"
        class="comment-content--reference teld-color-lightgray truncate mb-1 pl-2"
        style="border-left: 4px solid lightgray"
      >
        {{ comment.ReferenceTitle }}
      </div>
      <div
        v-if="!comment.IsDeleted"
        v-html="decodeURIComponent(comment.Content)"
        @click="onClickContent"
      ></div>
      <div v-else class="teld-color-gray">该条评论已删除</div>
    </div>

    <!-- 评论底部按钮行 -->
    <!-- <div class="flex justify-between">
      <span :title="comment.CommentTime" class="teld-color-gray">{{ comment.CommentTimeRelative }}</span>
    </div> -->
    <div
      v-if="!comment.IsDeleted"
      class="comment-operations flex justify-end mb-3"
      :class="[disabled ? 'teld-color-lightgray' : 'teld-color-gray']"
    >
      <!-- 点赞 -->
      <div
        :tabindex="disabled ? -1 : 0"
        class="comment-like flex outline-on-focus rounded-sm leading-5 hover:text-theme-color"
        :class="[disabled ? 'cursor-not-allowed' : 'cursor-pointer']"
        @click="onClickLike"
      >
        <SvgHeartFilled v-show="comment.Liked" class="mr-1" />
        <SvgHeart v-show="!comment.Liked" class="mr-1" />
        <span class="">{{ comment.LikeCount || 0 }}</span>
      </div>
      <!-- 收藏 -->
      <div
        :tabindex="disabled ? -1 : 0"
        class="comment-star flex ml-2 outline-on-focus rounded-sm leading-5 hidden"
        :class="[disabled ? 'cursor-not-allowed' : 'cursor-pointer']"
        @click="onClickFavorite"
      >
        <SvgStarFilled v-show="comment.IsFavorite" class="mr-1" />
        <SvgStar v-show="!comment.IsFavorite" class="mr-1" />
        <span class="">
          {{ comment.FavoriteCount || 0 }}
        </span>
      </div>
      <!-- 回复 -->
      <div
        :tabindex="disabled ? -1 : 0"
        class="comment-reply ml-2 outline-on-focus rounded-sm hover:text-theme-color"
        :class="[
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          inputVisible ? 'teld-theme-color' : 'teld-color-gray',
        ]"
        @click="onClickReply"
      >
        回复
      </div>
      <!-- 删除 -->
      <div
        v-if="comment.CanCancel && !disabled"
        tabindex="0"
        class="comment-revoke ml-2 outline-on-focus rounded-sm hover:text-theme-color"
        @click="onClickRevoke"
      >
        删除
      </div>
    </div>

    <!-- 附件 -->
    <section
      v-if="comment.Attachments && comment.Attachments.length"
      class="attachments-section mb-2 border border-solid border-gray-100 rounded-sm"
    >
      <div
        v-for="attachment in comment.Attachments"
        :key="attachment.ID"
        class="attachment-file p-2 flex items-center"
        @click="handleDownloadFile(attachment)"
      >
        <!-- 文件类型图标 -->
        <FileTypeIcon :file-name="attachment.FileName" class="w-6 h-6 mr-2" />
        <!-- 文件名 -->
        <div
          class="flex-1 mr-2 van-multi-ellipsis--l2 text-xs overflow-hidden"
          :title="attachment.FileName"
        >
          {{ attachment.FileName }}
        </div>
      </div>
    </section>

    <!-- 评论的回复框 -->
    <draft-editor
      ref="draftEditorRef"
      :visible.sync="inputVisible"
      :placeholder="inputPlaceholder"
      class="h-0"
      @comment="onReplyToComment"
    />

    <!-- 本评论的回复列表Top2 -->
    <div v-if="!comment.IsDeleted" class="reply-list mt-2">
      <ReplyItem
        v-for="reply in comment.RepliesTop2"
        :key="reply.ID"
        :data-item="reply"
        @reply="onReplyToReply"
        @delete="onReplyDelete"
      />
    </div>

    <div
      v-if="!comment.IsDeleted && comment.ReplyCount > 2"
      :tabindex="disabled ? -1 : 0"
      class="
        self-start px-3 py-2 teld-bg-color-gray teld-color-gray hover:text-theme-color rounded-sm outline-on-focus
      "
      :class="[disabled ? 'cursor-not-allowed' : 'cursor-pointer']"
      @click="showAllReplies"
    >
      查看全部 {{ comment.ReplyCount }} 条回复
    </div>

    <CommentRepliesPopup
      :visible.sync="commentRepliesPopupVisible"
      :data-item="comment"
      @reply="onAddReplyInPopup"
      @delete-reply="onDeleteReplyInPopup"
    ></CommentRepliesPopup>

    <PersonnelInfoPopup
      :visible.sync="personnelInfoVisible"
      :user-id.sync="mentionedUserId"
    />
  </div>
</template>

<script>
// Components
import SvgHeart from "../../svg-icons/heart.vue";
import SvgHeartFilled from "../../svg-icons/heart-filled.vue";
import SvgStar from "../../svg-icons/star.vue";
import SvgStarFilled from "../../svg-icons/star-filled.vue";
import ReplyItem from '../../mobile/reply-item/index.vue';
import DraftEditor from '../draft-editor/index.vue';
import CommentRepliesPopup from '../comment-replies-popup/index.vue';
import FileTypeIcon from '../../file-type-icon/index.vue';
import PersonnelInfoPopup from '../personnel-info-popup/index.vue';

// Utils
import { relativeTime, saveAs } from "../../../core";
import dayjs from 'dayjs';

export default {
  name: "CommentItem",
  inject: [
    'loginUserInfo',
    'pageUrl',
    'showCommentReference',
    'notificationLink',
  ],
  props: {
    dataItem: {
      type: Object,
      default: () => {},
    },
  },
  components: {
    SvgHeart,
    SvgHeartFilled,
    SvgStar,
    SvgStarFilled,
    ReplyItem,
    DraftEditor,
    CommentRepliesPopup,
    FileTypeIcon,
    PersonnelInfoPopup,
  },
  data() {
    return {
      inputVisible: false,
      comment: {},
      commentRepliesPopupVisible: false,
      previewSrcList: [],
      //
      personnelInfoVisible: false,
      mentionedUserId: null,
    }
  },
  computed: {
    disabled() {
      return this.comment.IsDeleted;
    },
    isAuthor() {
      const loginUser = this.loginUserInfo();
      return loginUser.userId === this.comment.UserID;
    },
    userNameDisplay() {
      return this.isAuthor ? '我' : this.comment.UserName;
    },
    inputPlaceholder() {
      return `回复 ${this.userNameDisplay}`;
    },
  },
  created() {
    const { CommentTime, ReplyCount, Replies } = this.dataItem;
    const timeRelative = relativeTime(CommentTime);
    const top2Replies = ReplyCount > 2 ? Replies.slice(0,2) : Replies;

    this.comment = {
      ...this.dataItem,
      CommentTimeRelative: timeRelative || "",
      RepliesTop2: top2Replies,
      CommentTime: dayjs(CommentTime).format('YYYY/MM/DD HH:mm:ss'),
    };
  },
  mounted() {
    this.initImagePreview();
  },
  methods: {
    initImagePreview() {
      const elImages = this.$el.querySelectorAll('.comment-content img');
      this.previewSrcList = [].map.call(elImages, (elImage) => {
        return elImage.src;
      })
    },
    async onClickLike() {
      if(this.disabled) return;
      const isLike = this.comment.Liked = !this.comment.Liked;
      const diff = isLike ? 1 : -1;
      this.comment.LikeCount += diff;
      let succeeded = false;

      const payload = isLike ? { CommentID: this.comment.ID, TargetID: 0 } : { Id: this.comment.ID };
      const serviceCode = isLike ? 'CMTS-Like' : 'CMTS-CancelLike';
      try {
        const { data } = await this.$utils.sgApi.getDataAsync({
          url: this.$utils.common.getServiceUrl(serviceCode),
          data: isLike ? { comment: JSON.stringify(payload) } : { input: JSON.stringify(payload) },
        });

        if (data.state !== '1') {
          return;
        }

        succeeded = true;
        // this.$TM.Toast(isLike ? '已点赞' : '已取消点赞');
      } catch (error) {
        console.log(error);
      } finally {
        // 接口调用失败后，页面状态回滚
        if (!succeeded) {
          this.comment.Liked = !isLike;
          this.comment.LikeCount -= diff;
        }
      }
    },
    async onClickFavorite() {
      if(this.disabled) return;
      this.comment.IsFavorite = !this.comment.IsFavorite;
      const diff = this.comment.IsFavorite ? 1 : -1;
      this.comment.FavoriteCount += diff;
      // TODO 调接口
    },
    onClickReply() {
      if (this.disabled) return;
      this.inputVisible = true;
    },
    /** 点击删除（评论） */
    async onClickRevoke() {
      if (this.disabled) return;

      // 确认
      try {
        await this.$TM.Dialog.confirm({
          title: '是否确定删除',
          showCancelButton: true,
          closeOnClickOverlay: true,
          confirmButtonColor: 'var(--theme-color)',
        });
      } catch (error) {
        // cancel
        return;
      }

      this.comment.IsDeleted = true;
      const onFail = () => {
        this.comment.IsDeleted = false;
        this.comment.CanCancel = false;
        this.$TM.Toast('删除失败');
      }

      try {
        const { data } = await this.$utils.sgApi.getDataAsync({
          url: this.$utils.common.getServiceUrl('CMTS-CancelComment'),
          data: { id: this.comment.ID },
        });

        if (data.state !== '1') {
          onFail();
          return;
        }
        this.$TM.Toast('已删除');
        this.$emit('decrease', this.comment.ReplyCount + 1);
      } catch (error) {
        console.log(error);
        onFail();
      }
    },
    async onReplyToComment(draftInfo) {
      const loginUser = this.loginUserInfo();
      const { content, fileList, onSucceed } = draftInfo;
      const newCommentReply = {
        // 接口必传字段：
        CommentID: this.comment.ID,
        Content: content,
        Dimension: "", //
        UserAvatar: loginUser.avatar,
        Attachments: fileList.map(n => n.result),
        Url: this.notificationLink(),
        UrlParams: JSON.stringify({
          fpath: encodeURIComponent(this.pageUrl),
          showComment: true,
          commentId: this.comment.ID,
        }),
      }

      try {
        const { data } = await this.$utils.sgApi.getDataAsync({
          url: this.$utils.common.getServiceUrl('CMTS-SaveReply'),
          data: { reply: JSON.stringify(newCommentReply) },
        });
        console.log('SaveReply', data);
        if (data.state !== '1' || !data.data) {
          this.$TM.Toast('发送失败');
          return;
        }
        this.$TM.Toast('发送成功');

        // TODO 接口没有返回新数据对象，暂时用前端数据
        Object.assign(newCommentReply, {
          ID: 'comment-reply-m-' + parseInt(Math.random() * 1000), // TODO UI-mock
          ReplyTime: Date().toLocaleString(), // TODO UI-mock
          UserID: loginUser.userId, // TODO UI-mock
          UserName: loginUser.userName, // TODO UI-mock
          Liked: false, // TODO UI-mock
          LikeCount: 0, // TODO UI-mock
          IsFavorite: false, // TODO UI-mock
          FavoriteCount: 0, // TODO UI-mock
          IsDeleted: false, // TODO UI-mock
          CanCancel: true, // TODO UI-mock
        });

        // 调接口成功后清空输入框内容
        onSucceed();
        this.inputVisible = false;
        // 评论完将新添加评论展示出来
        this.comment.RepliesTop2.unshift((data && data.data) || newCommentReply);
        this.comment.ReplyCount += 1;

        this.$emit('increase');
      } catch (error) {
        console.log(error);
        this.$TM.Toast('发送失败');
      }
    },
    onReplyToReply(data) {
      this.comment.RepliesTop2.unshift(data);
      this.comment.ReplyCount += 1;
      this.$emit('increase');
    },
    onReplyDelete(data) {
      this.$emit('decrease');
    },
    showAllReplies() {
      if (this.disabled) return;
      this.commentRepliesPopupVisible = true;
    },
    onAddReplyInPopup() {
      this.comment.ReplyCount += 1;
      this.$emit('increase');
    },
    onDeleteReplyInPopup(data) {
      this.onReplyDelete(data);
    },
    onClickContent(e) {
      const el = e.target;
      // 如果点击了图片，则放大查看
      if (el.nodeName === 'IMG' && el.currentSrc) {
        this.$TM.ImagePreview({images:[el.currentSrc]});
      } else if(el.dataset.userId) {
        // 点击了提及人，则弹出人员信息
        this.mentionedUserId = el.dataset.userId;
        this.personnelInfoVisible = true;
      } else {
        // 点击了内容，则直接弹出回复框
        this.onClickReply();
      }
    },
    handleDownloadFile(attachment) {
      const { AttachmentUrl, FileName } = attachment;
      saveAs({
        url: AttachmentUrl,
        fileName: FileName,
      });
    },
  },
};
</script>

<style lang="scss" scoped></style>
